import { useContext } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { createSegment } from 'api/segments';
import { FilterProviderContext } from 'components/filter/filters-next/FilterProvider';
import { SegmentContext } from 'components/filter/filters-next/SegmentContext';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { ModelType } from 'components/filter/types-legacy';
import * as Form from 'components/form';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { segmentQueryKeys } from 'queries/segment-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface SegmentFormValues {
    name: string;
}

interface SaveAsNewSegmentDialogProps extends AlertDialog.AlertDialogRootProps {}

const SaveAsNewSegmentDialog = (props: SaveAsNewSegmentDialogProps) => {
    const { children, ...rest } = props;
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { success, error } = useToast();
    const { resource, cacheKey } = useContext(FilterProviderContext);
    const { setIsEditing } = useContext(SegmentContext);
    const setSegmentId = useFiltersCache(state => state.setSegmentIdByKey);
    const { filters } = useFiltersByKey(cacheKey);
    const methods = Form.useGivebutterForm<SegmentFormValues>({
        defaultValues: {
            name: '',
        },
    });
    const { control, path, handleSubmit, reset, resetFormButton, setError } = methods;

    const { isPending, mutate: submit } = useMutation({
        mutationFn: (values: SegmentFormValues) =>
            createSegment({
                accountId,
                filters,
                name: values.name,
                type: resource as ModelType,
            }),
        onSuccess: seg => {
            success('Created segment successfully!');
            setIsEditing(false);
            setSegmentId(cacheKey, seg.id);
            reset();
            queryClient.invalidateQueries({
                queryKey: segmentQueryKeys.all(accountId, resource as ModelType),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            const errors = e.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    setError(key as keyof SegmentFormValues, {
                        message: errors[key][0],
                    });
                });
            } else {
                error(e.response?.data?.message ?? `Unable to create segment`);
            }
            resetFormButton();
        },
    });

    return (
        <AlertDialog.Root type={'info'} {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <Form.Provider {...methods}>
                        <form
                            className={'contents'}
                            onSubmit={handleSubmit(submit as SubmitHandler<SegmentFormValues>)}
                        >
                            <AlertDialog.Header>
                                <AlertDialog.Icon />
                                <AlertDialog.Close />
                            </AlertDialog.Header>
                            <AlertDialog.Body>
                                <AlertDialog.Title>Save as new segment</AlertDialog.Title>
                                <Form.Container>
                                    <Form.Label name={path('name')}>Segment name</Form.Label>
                                    <Form.TextInputRoot
                                        control={control}
                                        name={'name'}
                                        rules={{ required: 'A segment must have a name' }}
                                    >
                                        <Form.TextInputForm placeholder={'Enter a segment name'} />
                                    </Form.TextInputRoot>
                                    <Form.Error name={path('name')} />
                                </Form.Container>
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <div
                                    className={
                                        'mt-4 flex w-full flex-wrap-reverse items-center gap-3'
                                    }
                                >
                                    <div className={'flex-1'}>
                                        <AlertDialog.Cancel className="w-full">
                                            Cancel
                                        </AlertDialog.Cancel>
                                    </div>
                                    <div className={'flex-1'}>
                                        <Form.SubmitButton
                                            disabled={filters.length === 0}
                                            isSubmitting={isPending}
                                            className={'w-full'}
                                        >
                                            Save
                                        </Form.SubmitButton>
                                    </div>
                                </div>
                            </AlertDialog.Footer>
                        </form>
                    </Form.Provider>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const SaveAsNewSegmentDialogTrigger = AlertDialog.Trigger;
const Root = SaveAsNewSegmentDialog;
const Trigger = SaveAsNewSegmentDialogTrigger;

export {
    SaveAsNewSegmentDialog,
    SaveAsNewSegmentDialogTrigger,
    //
    Root,
    Trigger,
};
